import {
  HeatingOrCoolingSource,
  PerformanceTableFragment,
} from '../../../generated/graphql'
import { getPerformanceFromTable } from '../util/performance-table-utility'
import {
  calculateHeatCollectionTemperature,
  createHeatSourceFromHeatingSource,
  createHeatSourceFromWasteSource,
  HeatSource,
} from './heating-util'
import { reduceToSum } from '../../../util/array-utility'
import { calculateUsedCapacity, HeatingHeatPumpResults } from './heat-pump-util'

export type HeatingCalculationParams = {
  performanceTable: PerformanceTableFragment
  copModifier: number
  heatSourceAvailable: boolean
  heatingPower: number
  baseHeatSource?: HeatingOrCoolingSource
  groundHeatSourceTemperature?: number
  groundHeatSourceMaxPower?: number
  airHeatSourceMinTemperature?: number
  wasteHeatSourceAPower?: number
  wasteHeatSourceATemperature?: number
  wasteHeatSourceBPower?: number
  wasteHeatSourceBTemperature?: number
  basicHeatingTemperature?: number
  dhwHeatingTemperature?: number
  basicHeatingDemand?: number
  dhwHeatingDemand?: number
  powerCapacity: number
  outdoorTemperature: number
}

export type HeatingCalculationResults = HeatingHeatPumpResults & {
  heatSources: HeatSource[]
  powerCapacity: number
}

function estimateTotalCollectedHeat(
  heatingPower: number,
  powerCapacity: number,
  heatingDemand: number,
): number {
  return Math.min(heatingDemand, heatingPower * powerCapacity) * 0.66
}

function allocateCollectedHeatToSources(
  collectedHeat: number,
  heatSources: HeatSource[],
): HeatSource[] {
  if (heatSources.length === 0) {
    return []
  }

  const previouslyCollectedHeatForSource = heatSources[0].collectedHeat ?? 0
  const availableHeatFromSource =
    heatSources[0].maxPower - previouslyCollectedHeatForSource
  const collectedHeatForSource = Math.min(
    availableHeatFromSource,
    collectedHeat,
  )

  return [
    {
      ...heatSources[0],
      collectedHeat: previouslyCollectedHeatForSource + collectedHeatForSource,
    },
    ...allocateCollectedHeatToSources(
      collectedHeat - collectedHeatForSource,
      heatSources.slice(1),
    ),
  ]
}

function estimateHeatCollectionTemperature(
  heatSources: HeatSource[],
  estimatedTotalCollectedHeat: number,
): number {
  const allocatedHeatSources = allocateCollectedHeatToSources(
    estimatedTotalCollectedHeat,
    heatSources,
  )

  return calculateHeatCollectionTemperature(allocatedHeatSources)
}

function heat(
  cop: number,
  powerCoefficient: number,
  heatingPower: number,
  heatSources: HeatSource[],
  heatingDemand: number,
  powerCapacity: number,
) {
  if (Number.isNaN(cop) || Number.isNaN(powerCoefficient)) {
    return { heatingDemand, providedHeating: 0, powerCapacity, heatSources }
  }

  const heatCollectionPotential = heatSources
    .map((heatSource) => heatSource.maxPower - (heatSource.collectedHeat ?? 0))
    .reduce(reduceToSum, 0)

  const providedHeating = Math.min(
    heatingDemand,
    heatingPower * powerCapacity * powerCoefficient,
    (heatCollectionPotential * cop) / (cop - 1),
  )

  const collectedHeat = (providedHeating * (cop - 1)) / cop

  const usedCapacity = calculateUsedCapacity(
    providedHeating,
    heatingPower,
    powerCoefficient,
  )

  return {
    heatingDemand: heatingDemand - providedHeating,
    providedHeating,
    heatSources: allocateCollectedHeatToSources(collectedHeat, heatSources),
    powerCapacity: powerCapacity - usedCapacity,
  }
}

export function calculateHeatingForHour({
  performanceTable,
  copModifier,
  heatSourceAvailable,
  heatingPower,
  baseHeatSource,
  groundHeatSourceTemperature,
  groundHeatSourceMaxPower,
  airHeatSourceMinTemperature,
  wasteHeatSourceAPower,
  wasteHeatSourceATemperature,
  wasteHeatSourceBPower,
  wasteHeatSourceBTemperature,
  basicHeatingTemperature,
  dhwHeatingTemperature,
  basicHeatingDemand,
  dhwHeatingDemand,
  powerCapacity,
  outdoorTemperature,
}: HeatingCalculationParams): HeatingCalculationResults {
  const totalHeatingDemand = (basicHeatingDemand ?? 0) + (dhwHeatingDemand ?? 0)

  const heatSources: HeatSource[] = [
    createHeatSourceFromHeatingSource(
      baseHeatSource,
      groundHeatSourceTemperature,
      groundHeatSourceMaxPower,
      airHeatSourceMinTemperature,
      totalHeatingDemand,
      outdoorTemperature,
    ),
    createHeatSourceFromWasteSource(
      wasteHeatSourceAPower,
      wasteHeatSourceATemperature,
    ),
    createHeatSourceFromWasteSource(
      wasteHeatSourceBPower,
      wasteHeatSourceBTemperature,
    ),
  ]
    .filter((heatSource): heatSource is HeatSource => heatSource !== null)
    // Sort by temperature (descending).
    .sort((a, b) => (a.temperature > b.temperature ? -1 : 1))

  if (!heatSourceAvailable || totalHeatingDemand === 0) {
    return {
      basic: {
        totalDemand: basicHeatingDemand ?? 0,
        providedHeating: 0,
        cop: NaN,
      },
      dhw: {
        totalDemand: dhwHeatingDemand ?? 0,
        providedHeating: 0,
        cop: NaN,
      },
      powerCapacity,
      heatSources,
    }
  }

  // Calculate heat collection temperature

  const estimatedTotalCollectedHeat = estimateTotalCollectedHeat(
    heatingPower,
    powerCapacity,
    totalHeatingDemand,
  )
  const heatCollectionTemperature = estimateHeatCollectionTemperature(
    heatSources,
    estimatedTotalCollectedHeat,
  )

  // Get COPs and power coefficients from performance tables

  const basicPerformance = basicHeatingTemperature
    ? getPerformanceFromTable(
        performanceTable,
        heatCollectionTemperature,
        basicHeatingTemperature,
        copModifier,
      )
    : null
  const dhwPerformance = dhwHeatingTemperature
    ? getPerformanceFromTable(
        performanceTable,
        heatCollectionTemperature,
        dhwHeatingTemperature,
        copModifier,
      )
    : null

  // Calculate basic heating

  const {
    heatingDemand: totalBasicHeatingDemand,
    providedHeating: providedBasicHeating,
    heatSources: heatSourcesAfterBasicHeating,
    powerCapacity: powerCapacityAfterBasicHeating,
  } = heat(
    basicPerformance?.cop ?? NaN,
    basicPerformance?.powerCoefficient ?? NaN,
    heatingPower,
    heatSources,
    basicHeatingDemand ?? 0,
    powerCapacity,
  )

  // Calculate DHW heating

  const {
    heatingDemand: totalDhwHeatingDemand,
    providedHeating: providedDhwHeating,
    heatSources: heatSourcesAfterDhwHeating,
    powerCapacity: powerCapacityAfterDhwHeating,
  } = heat(
    dhwPerformance?.cop ?? NaN,
    dhwPerformance?.powerCoefficient ?? NaN,
    heatingPower,
    heatSourcesAfterBasicHeating,
    dhwHeatingDemand ?? 0,
    powerCapacityAfterBasicHeating,
  )

  return {
    basic: {
      totalDemand: totalBasicHeatingDemand,
      providedHeating: providedBasicHeating,
      cop: basicPerformance?.cop ?? NaN,
    },
    dhw: {
      totalDemand: totalDhwHeatingDemand,
      providedHeating: providedDhwHeating,
      cop: dhwPerformance?.cop ?? NaN,
    },
    heatSources: heatSourcesAfterDhwHeating,
    powerCapacity: powerCapacityAfterDhwHeating,
  }
}
